import { useState } from 'react'
import type { JobType } from '@workflow/contracts'
import { Button, ConfirmButton, EmptyNote, Field, Icons, Loading, StateNote, SwitchPanel, Tabs, TextField } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import {
  useAllJobTypes,
  useCreateJobType,
  useRestoreJobType,
  useRetireJobType,
  useSession,
  useUpdateJobType,
} from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'
import { LevelsPane } from './LevelsPane'

/**
 * The studio's own vocabulary, administered: the kinds of work jobs are
 * named by. An administrator adds, renames, re-prices, retires and restores
 * them; staff read the living list in the pickers. A retired kind keeps its
 * name on the work already under it and is offered to nothing new until it
 * is brought back — retiring is a decision on the record, not a deletion.
 */

export function WorkTypesPane() {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<JobType | null>(null)
  // Two vocabularies live here: the kinds of work, and the levels people stand on.
  const [part, setPart] = useState<'work' | 'levels'>('work')

  if (!isAdmin) {
    return (
      <StateNote
        title="This part of settings is for administrators."
        detail="The categories are the studio's shared vocabulary. Administrators shape it; staff read it in the pickers."
      />
    )
  }

  // The switch stays mounted above both views, so its underline glides
  // between them instead of being rebuilt with each one.
  return (
    <div className="flex flex-col gap-6">
      <Tabs<'work' | 'levels'>
        label="Categories"
        value={part}
        onChange={setPart}
        options={[
          { value: 'work', label: 'Work' },
          { value: 'levels', label: 'Levels' },
        ]}
      />
      <SwitchPanel activeKey={part}>
        {part === 'levels' ? (
          <LevelsPane />
        ) : (
          <WorkCategories
            creating={creating}
            editing={editing}
            onCreate={() => setCreating(true)}
            onEdit={setEditing}
            onClose={() => {
              setCreating(false)
              setEditing(null)
            }}
          />
        )}
      </SwitchPanel>
    </div>
  )
}

function WorkCategories({ creating, editing, onCreate, onEdit, onClose }: {
  creating: boolean
  editing: JobType | null
  onCreate: () => void
  onEdit: (type: JobType) => void
  onClose: () => void
}) {
  const types = useAllJobTypes()
  const living = (types.data ?? []).filter((type) => type.retiredAt === null)
  const retired = (types.data ?? []).filter((type) => type.retiredAt !== null)

  return (
    <div className="flex flex-col gap-6">
      {creating || editing ? (
        <TypeForm
          initial={editing ?? undefined}
          onDone={onClose}
          onCancel={onClose}
        />
      ) : (
        <div>
          <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={onCreate}>
            Add a category
          </Button>
          <p className="mt-1.5 text-caption text-muted-foreground">Jobs are sorted by category.</p>
        </div>
      )}

      <div>
        <h3 className="text-micro text-muted-foreground uppercase">In use</h3>
        <div className="mt-1.5">
          {types.isLoading ? (
            <Loading label="Loading categories" />
          ) : types.error ? (
            <StateNote title="The categories could not be loaded." detail={describeError(types.error)} />
          ) : living.length === 0 ? (
            <EmptyNote title="No categories yet." detail="Add the first category — every job belongs to one." />
          ) : (
            <ul className="flex flex-col">
              {living.map((type) => (
                <TypeRow key={type.id} type={type} onEdit={() => onEdit(type)} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {retired.length > 0 ? (
        <div>
          <h3 className="text-micro text-muted-foreground uppercase">Retired</h3>
          <ul className="mt-1.5 flex flex-col">
            {retired.map((type) => (
              <RetiredRow key={type.id} type={type} />
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/** The creation and the mid-edit: one card, the name required, the rest as filed. */
function TypeForm({
  initial,
  onDone,
  onCancel,
}: {
  initial?: JobType
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [rate, setRate] = useState(initial?.defaultRate == null ? '' : String(initial.defaultRate / 100))
  const create = useCreateJobType()
  const update = useUpdateJobType()

  const busy = create.isPending || update.isPending
  const error = create.error ?? update.error
  const rateInvalid = rate.trim() !== '' && (!Number.isFinite(Number(rate)) || Number(rate) < 0)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const input = {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : { description: null }),
      ...(rate.trim() !== '' && Number.isFinite(Number(rate)) ? { defaultRate: Math.round(Number(rate) * 100) } : { defaultRate: null }),
    }
    if (initial) {
      update.mutate({ id: initial.id, input }, { onSuccess: onDone })
    } else {
      create.mutate(input, { onSuccess: onDone })
    }
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border bg-surface px-5 py-4 shadow-chip">
      <Field label="Name" htmlFor="work-type-name" hint="What the jobs ledger calls this category.">
        <TextField
          id="work-type-name"
          value={name}
          autoFocus
          placeholder="e.g. Playtesting"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label="Description" htmlFor="work-type-description" hint="Optional — one line on what the work is.">
        <TextField
          id="work-type-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <Field
        label="Default rate"
        htmlFor="work-type-rate"
        hint="Optional — the hourly rate suggested for this category, in major units (e.g. 45.50)."
        error={rateInvalid ? 'The rate is a number.' : undefined}
      >
        <TextField
          id="work-type-rate"
          value={rate}
          placeholder="e.g. 45.50"
          onChange={(event) => setRate(event.target.value)}
        />
      </Field>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" variant="primary" busy={busy} disabled={!name.trim() || rateInvalid}>
          {initial ? 'Save category' : 'Add category'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {error ? <p className="text-caption text-danger">{describeError(error)}</p> : null}
      </div>
    </form>
  )
}

function TypeRow({ type, onEdit }: { type: JobType; onEdit: () => void }) {
  const retire = useRetireJobType()
  return (
    <li className="border-b border-hairline last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-2.5">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui font-medium text-foreground">{type.name}</span>
          {type.description ? (
            <span className="block truncate text-caption text-muted-foreground">{type.description}</span>
          ) : null}
        </span>
        {type.defaultRate !== null ? (
          <span className="text-caption text-muted-foreground" title="Default hourly rate">
            {(type.defaultRate / 100).toFixed(2)} / hour
          </span>
        ) : null}
        <Button size="xs" variant="ghost" onClick={onEdit}>
          Edit
        </Button>
        <ConfirmButton
          label="Retire"
          confirmLabel="Really retire?"
          busy={retire.isPending}
          onConfirm={() => retire.mutate({ id: type.id })}
        />
      </div>
      {retire.error ? <p className="px-1 pb-2 text-caption text-danger">{describeError(retire.error)}</p> : null}
    </li>
  )
}

function RetiredRow({ type }: { type: JobType }) {
  const restore = useRestoreJobType()
  return (
    <li className="border-b border-hairline last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-2.5">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui font-medium text-muted-foreground">{type.name}</span>
          {type.description ? (
            <span className="block truncate text-caption text-muted-foreground">{type.description}</span>
          ) : null}
        </span>
        <span className="text-caption text-muted-foreground" title="Retired">
          Retired {formatTimestamp(type.retiredAt!)}
        </span>
        <Button size="xs" variant="ghost" busy={restore.isPending} onClick={() => restore.mutate({ id: type.id })}>
          Restore
        </Button>
      </div>
      {restore.error ? <p className="px-1 pb-2 text-caption text-danger">{describeError(restore.error)}</p> : null}
    </li>
  )
}
